import {
    styled,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@mui/material';
import { api, formationContinueEntryType } from '../../../lib/api/api';
import { IconButton } from '../../../components/IconButton';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAlert } from '../../../lib/alert';
import { Query } from '../../../components/Query';
import {
    formationWithOptionSlugType,
    selectedTrainingType,
    TRAINING_WITH_OPTIONS_SLUGS,
    TRAINING_WITHOUT_OPTIONS_SLUGS,
} from '../../types';
import {
    formationWithOptionsMapping,
    formationWithoutOptionsMapping,
    fundingMapping,
} from '../../constants';
import { convertToFrenchDate } from '../../../lib/utils';
import { formationContinuePricesHandler } from '../lib/formationContinuePricesHandler';
import { useNavigate } from 'react-router-dom';
import { pathHandler } from '../../../lib/pathHandler';
import { useState } from 'react';
import { convertFrenchDateToISO } from '../../../lib/date';

type orderByColumnType = 'createdAt' | 'email' | 'firstName' | 'lastName' | 'fundingKind';

const JOB_REGEX = /^(.*)\|(.*)\((.+)\)$/;
type orderByType = { column: orderByColumnType; direction: 'asc' | 'desc' };

function FormationContinueEntryTable() {
    const { displayAlert } = useAlert();
    const [orderBy, setOrderBy] = useState<orderByType | undefined>({
        column: 'createdAt',
        direction: 'desc',
    });
    const navigate = useNavigate();

    return (
        <Query queryKey={['formation-continue-entries']} apiCall={api.getFormationContinueEntries}>
            {(data) => (
                <>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy?.column === 'createdAt'}
                                        direction={orderBy?.direction}
                                        onClick={buildSortHandler('createdAt')}
                                    >
                                        Date de demande
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy?.column === 'firstName'}
                                        direction={orderBy?.direction}
                                        onClick={buildSortHandler('firstName')}
                                    >
                                        Prénom
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy?.column === 'lastName'}
                                        direction={orderBy?.direction}
                                        onClick={buildSortHandler('lastName')}
                                    >
                                        Nom
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy?.column === 'email'}
                                        direction={orderBy?.direction}
                                        onClick={buildSortHandler('email')}
                                    >
                                        E-mail
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Téléphone</TableCell>
                                <TableCell>Adresse</TableCell>
                                <TableCell>Fonction</TableCell>
                                <TableCell>Structure</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy?.column === 'fundingKind'}
                                        direction={orderBy?.direction}
                                        onClick={buildSortHandler('fundingKind')}
                                    >
                                        Financement souhaité
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Modules(s) souhaité(s)</TableCell>
                                <TableCell>Coût total formation</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortData(data).map((formationContinueEntry) => {
                                const rowData = computeRowData(formationContinueEntry);

                                return (
                                    <TableRow key={formationContinueEntry.id}>
                                        {rowData.map((cellContent, index) => {
                                            const splitCellContent = cellContent.split('\n');
                                            if (splitCellContent.length === 1) {
                                                return (
                                                    <TableCell key={index}>{cellContent}</TableCell>
                                                );
                                            } else {
                                                return (
                                                    <TableCell key={index}>
                                                        <ul>
                                                            {splitCellContent.map(
                                                                (line, lineIndex) => (
                                                                    <li key={lineIndex}>{line}</li>
                                                                ),
                                                            )}
                                                        </ul>
                                                    </TableCell>
                                                );
                                            }
                                        })}
                                        <TableCell>
                                            <CellRow>
                                                <IconButton
                                                    color="primary"
                                                    size="small"
                                                    title="Copier la ligne"
                                                    IconComponent={ContentCopyIcon}
                                                    onClick={() => copyRowToClipboard(rowData)}
                                                />
                                                <IconButton
                                                    color="primary"
                                                    size="small"
                                                    title="Aller au formulaire"
                                                    IconComponent={ArrowForwardIcon}
                                                    onClick={() =>
                                                        navigateToEntryForm(
                                                            formationContinueEntry.id,
                                                        )
                                                    }
                                                />
                                            </CellRow>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </>
            )}
        </Query>
    );

    function navigateToEntryForm(id: string) {
        const path = pathHandler.getRoutePath('FORMATION_ENTRY', {
            id,
            kind: 'formation-continue',
        });
        navigate(path);
    }

    function sortData(data: formationContinueEntryType[]) {
        if (orderBy === undefined) {
            return data;
        }
        return data.sort((a, b) => {
            const aValue = a[orderBy.column];
            const bValue = b[orderBy.column];
            if (aValue === bValue) {
                return 0;
            }
            let comparison = 0;
            switch (orderBy.column) {
                case 'createdAt':
                    comparison =
                        new Date(convertFrenchDateToISO(aValue)).getTime() -
                            new Date(convertFrenchDateToISO(bValue)).getTime() >
                        0
                            ? 1
                            : -1;
                    break;
                case 'email':
                case 'firstName':
                case 'lastName':
                case 'fundingKind':
                default:
                    comparison = aValue.localeCompare(bValue);
            }

            return orderBy.direction === 'asc' ? comparison : -comparison;
        });
    }

    function buildSortHandler(orderByColumn: orderByColumnType) {
        return () => {
            if (orderBy === undefined || orderBy.column !== orderByColumn) {
                setOrderBy({ column: orderByColumn, direction: 'asc' });
                return;
            } else {
                switch (orderBy.direction) {
                    case 'asc':
                        setOrderBy({ column: orderByColumn, direction: 'desc' });
                        break;
                    case 'desc':
                        setOrderBy({ column: orderByColumn, direction: 'asc' });
                        break;
                }
            }
        };
    }

    function computeRowData(formationContinueEntry: formationContinueEntryType): string[] {
        let totalPrice = 0;
        const lastJob =
            formationContinueEntry.jobs.length > 0 ? formationContinueEntry.jobs[0] : undefined;
        let lastJobTitle = 'N/A';
        let lastJobStructure = 'N/A';
        if (lastJob) {
            const jobMatch = lastJob.match(JOB_REGEX);

            if (jobMatch) {
                lastJobTitle = jobMatch[1].trim();
                lastJobStructure = jobMatch[2].trim();
            }
        }
        const funding = fundingMapping[formationContinueEntry.fundingKind];
        const selectedTrainingsWithOptions: string[] = [];

        for (const TRAINING_WITH_OPTIONS_SLUG of TRAINING_WITH_OPTIONS_SLUGS) {
            const selectedTraining =
                formationContinueEntry.selectedTrainings[TRAINING_WITH_OPTIONS_SLUG];
            if (!selectedTraining) {
                continue;
            }
            const title = formationWithOptionsMapping[TRAINING_WITH_OPTIONS_SLUG].title;

            const selectedDate = computeSelectedDate(TRAINING_WITH_OPTIONS_SLUG, selectedTraining);
            selectedTrainingsWithOptions.push(
                `"${title}" (${selectedTraining.trainingOption}) - ${selectedDate}`,
            );
            if (selectedTraining.trainingOption === 'inter') {
                const price = formationContinuePricesHandler.computeFormationWithOptionsPrice(
                    TRAINING_WITH_OPTIONS_SLUG,
                    {
                        fundingKind: formationContinueEntry.fundingKind,
                    },
                );
                totalPrice += price;
            }
        }

        const selectedTrainingsWithoutOptions: string[] = [];
        for (const TRAINING_WITHOUT_OPTION_SLUG of TRAINING_WITHOUT_OPTIONS_SLUGS) {
            const selectedTraining =
                formationContinueEntry.selectedTrainings[TRAINING_WITHOUT_OPTION_SLUG];
            if (!selectedTraining) {
                continue;
            }
            const title = formationWithoutOptionsMapping[TRAINING_WITHOUT_OPTION_SLUG].title;
            const readableDate = convertToFrenchDate(selectedTraining.date);

            selectedTrainingsWithoutOptions.push(
                `"${title}" (${selectedTraining.trainingOption}) - ${readableDate}`,
            );
        }

        const selectedTrainings = [
            ...selectedTrainingsWithOptions,
            ...selectedTrainingsWithoutOptions,
        ];

        const selectedMaster = formationContinueEntry.selectedMaster;
        if (selectedMaster !== null) {
            const price = formationContinuePricesHandler.computeMasterPrice(selectedMaster);
            selectedTrainings.push(`Master ${formationContinueEntry.selectedMaster}`);
            totalPrice += price;
        }

        const discount = formationContinuePricesHandler.computeDiscountPrice(
            TRAINING_WITH_OPTIONS_SLUGS.filter(
                (slug) => formationContinueEntry.selectedTrainings[slug]?.date !== undefined,
            ),
            { fundingKind: formationContinueEntry.fundingKind },
        );
        if (discount > 0) {
            totalPrice -= discount;
        }

        const selectedTrainingsString = selectedTrainings.join('\n');

        return [
            formationContinueEntry.createdAt,
            formationContinueEntry.firstName,
            formationContinueEntry.lastName,
            formationContinueEntry.email,
            formationContinueEntry.phoneNumber,
            formationContinueEntry.address,
            lastJobTitle,
            lastJobStructure,
            funding.label,
            selectedTrainingsString,
            `${totalPrice} €`,
        ];
    }

    function computeSelectedDate(
        trainingSlug: formationWithOptionSlugType,
        selectedTraining: selectedTrainingType,
    ) {
        switch (selectedTraining.trainingOption) {
            case 'inter':
                return (
                    formationWithOptionsMapping[trainingSlug].dates.find(
                        (date) => date.value === selectedTraining.date,
                    )?.label || ''
                );
            case 'intra':
                return convertToFrenchDate(selectedTraining.date);
            default:
                return '';
        }
    }

    async function copyRowToClipboard(rowData: string[]) {
        const textToCopy = rowData.join('\t'); // Use tab as a separator for
        try {
            await navigator.clipboard.writeText(textToCopy);
            displayAlert({
                variant: 'success',
                text: 'Ligne copiée dans le presse-papiers.',
            });
        } catch (error) {
            displayAlert({
                variant: 'error',
                text: `Échec de la copie dans le presse-papiers: ${error}`,
            });
        }
    }
}

const CellRow = styled('div')(({ theme }) => ({
    display: 'flex',
}));

export { FormationContinueEntryTable };
